import { useCallback, useContext, useEffect, useRef } from 'react';
import { RendererContext } from '../../contexts/RendererContext';
import { useRerender } from '../useRerender';
import { Project } from '../../types/graphqlTypes';

export const useScreenTools = ({ project: _project }: { project?: Pick<Project, 'mapVisible'> }) => {
  const rendererContext = useContext(RendererContext);
  const viewer = rendererContext.viewer;
  const rerender = useRerender();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    viewer?.addEventListener('map_overlay_changed', rerender);
    return () => {
      viewer?.removeEventListener('map_overlay_changed', rerender);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!viewer]);

  // Press-and-hold zoom: one immediate step, then repeat after a short delay.
  const startZoom = useCallback(
    (direction: number) => {
      const controls = viewer?.orbitControls;
      if (!controls) return;
      controls.zoomBy(direction);
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        intervalRef.current = setInterval(() => controls.zoomBy(direction), 24);
      }, 500);
    },
    [viewer],
  );
  const onZoomOutStart = useCallback(() => startZoom(1), [startZoom]);
  const onZoomInStart = useCallback(() => startZoom(-1), [startZoom]);
  const onZoomEnd = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  // Stop any in-flight zoom repeat if we unmount mid-press, otherwise the
  // interval keeps mutating the viewer after this hook is gone.
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const onFullScreen = useCallback(() => {
    if (!document.fullscreenEnabled) return;
    if (!viewer) return;
    viewer.personaManager?.getActivePersona()?.resetToDefaultTool();
    document.getElementById('potree_render_area')?.requestFullscreen();
  }, [viewer]);

  const fitToScreen = useCallback(() => {
    viewer?.fitToScreen();
  }, [viewer]);

  const flip2DView = useCallback(() => {
    if (!viewer?.personaManager) return;
    const activePersona = viewer.personaManager.getActivePersona();
    if (!activePersona) return;
    activePersona.flipView();
  }, [viewer]);

  return [
    { fitToScreen, onFullScreen, flip2DView },
    { onZoomOutStart, onZoomInStart, onZoomEnd },
  ] as const;
};
